import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'types';
import { initialState } from './slice';
import {
  hasCustomerAssetMenu,
  hasCustomerEquipmentsAndDashboardMenus,
} from './utils';
import { selectUserDetails } from 'app/providers/AuthProvider/selectors';

const selectDomain = (state: RootState) =>
  state.customerControls || initialState;

export const selectCustomerControls = createSelector(
  [selectDomain],
  customerControlsState => customerControlsState,
);

export const selectAccountHasManyCustomers = createSelector(
  [selectDomain],
  ({ customers }) => {
    return customers.length > 1;
  },
);

export const selectDefaultCustomer = createSelector(
  [selectDomain],
  ({ customers }) => {
    return customers.length > 0 ? customers[0].id : '';
  },
);

export const selectCustomerByCountry = createSelector(
  [selectUserDetails, selectCustomerControls],
  (user, { customers }) => {
    const filteredCustomers =
      user?.allowedCountries !== null &&
      user?.allowedCountries.length &&
      user?.allowedCountries[0] !== ''
        ? customers.filter(customer =>
            user?.allowedCountries?.includes(customer.country || ''),
          )
        : customers;
    // ERIKS admin customer should not be visible to non-TAM users
    return user?.role !== 'TAM'
      ? filteredCustomers.filter(
          c => c.id !== 'c881bb65-2333-4d34-bccb-7b7e95c96d5d',
        )
      : filteredCustomers;
  },
);

export const selectDefaultCustomerForUser = createSelector(
  [selectDomain, selectCustomerByCountry, selectUserDetails],
  ({ customers }, customersByCountry, user) => {
    if (!user) return customers.length > 0 ? customers[0].id : '';
    return customersByCountry.length > 0 ? customersByCountry[0].id : '';
  },
);

export const selectSelectedCustomer = createSelector(
  [selectDomain],
  ({ selectedCustomer }) => selectedCustomer,
);

export const selectSelectedCustomerData = createSelector(
  [selectDomain],
  ({ selectedCustomer, customers }) => {
    return customers.find(({ id }) => id === selectedCustomer);
  },
);

export const selectCustomerHasAssetsMenu = createSelector(
  [selectSelectedCustomerData],
  customer => (customer ? hasCustomerAssetMenu(customer) : false),
);

export const selectCustomerHasEquipmentsAndDashboardMenus = createSelector(
  [selectSelectedCustomerData],
  customer =>
    customer ? hasCustomerEquipmentsAndDashboardMenus(customer) : false,
);

export const selectHasCustomerSelector = createSelector(
  [selectDomain],
  customerControlsState => customerControlsState.hasCustomerSelector,
);

export const selectLocations = createSelector(
  [selectDomain],
  customerControlsState => customerControlsState.locations,
);

export const selectLocationDetails = createSelector(
  [selectDomain, (_, locationId: string) => locationId],
  ({ locations }, locationId) => {
    if (!locations) return;
    return locations.find(location => location.id === locationId);
  },
);

export const selectLocationsLoading = createSelector(
  [selectDomain],
  customerControlsState => customerControlsState.locationsLoading,
);

export const selectHasLocationSelector = createSelector(
  [selectDomain],
  customerControlsState => customerControlsState.hasLocationSelector,
);

export const selectPath = createSelector(
  [selectDomain],
  customerControlsState => customerControlsState.path,
);

export const selectSelectedLocation = createSelector(
  [selectDomain],
  customerControlsState => customerControlsState.selectedLocation || undefined,
);

export const selectHomeLocation = createSelector(
  [selectDomain],
  ({ locations }) => {
    return locations && locations.length ? locations[0].id : '';
  },
);

export const customerHasLocation = createSelector(
  [selectDomain, (_, locationId: string) => locationId],
  ({ locations }, locationId) => {
    if (!locations) return false;
    return !!locations.find(location => location.id === locationId);
  },
);
